import { useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import { Oval } from "react-loader-spinner";

import { formatUSPhone } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import MultiSelectChipInput from "src/Pages/Setting/Report/components/MultiSelectChipInput";
import CustomerRoleService, {
  CUSTOMER_ROLE_INFO,
} from "src/Services/API/customerrole.service";

function AdminAddModal({
  customer,
  selectedAccount,
  onAddClicked,
  onCancelClicked,
}) {
  const currentUser = UserService.getCurrentUser();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [locationIds, setLocationIds] = useState([]);
  // The invitee's role in the customer apps. An Administrator has every
  // location, so only other roles (Manager) pick locations.
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(null);
  const role = roles.find((item) => item.id === roleId) ?? null;
  const needsLocations = !!role && role.name !== "Administrator";

  useEffect(() => {
    CustomerRoleService.getNames()
      .then((response) => setRoles(response.data || []))
      .catch(() => setRoles([]));
  }, []);
  const [submitted, setSubmitted] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // A Manager sees only the locations selected here.
  const locationOptions = useMemo(
    () =>
      (selectedAccount?.customers || [])
        .filter((location) => !location.isArchived)
        .map((location) => ({ id: location.id, name: location.companyName })),
    [selectedAccount],
  );

  const nameError = submitted && !name.trim();
  const emailError = submitted && !email.trim();
  const roleError = submitted && !role;
  const locationError = submitted && needsLocations && locationIds.length === 0;

  const addClicked = async () => {
    if (isInviting) return;
    setSubmitted(true);
    if (
      !name.trim() ||
      !email.trim() ||
      !role ||
      (needsLocations && locationIds.length === 0)
    )
      return;

    const data = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone,
      accountId: selectedAccount?.id,
      customerId: customer?.id,
      customerRoleId: role.id,
      // None for an Administrator: they have every location.
      customerLocationIds: needsLocations ? locationIds : [],
      invitedById: currentUser.id,
      company: currentUser.company,
    };
    setIsInviting(true);
    try {
      await onAddClicked(data);
    } finally {
      setIsInviting(false);
    }
  };

  const cancelClicked = () => {
    if (isInviting) return;
    onCancelClicked();
  };

  return (
    <div
      onClick={cancelClicked}
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[672px] max-w-full max-h-[90vh] bg-white rounded-[12px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E8F3]">
          <div className="flex flex-col">
            <h2 className="text-[20px] leading-[28px] capitalize font-OpenSansSemiBold text-[#1A2332]">
              Invite Customer User
            </h2>
            <p className="text-[14px] leading-[20px] font-OpenSansRegular text-[#868DA6]">
              Invite someone to the customer app as an Administrator or a
              Manager.
            </p>
          </div>
          <FiX
            size={18}
            className="text-[#868DA6] cursor-pointer"
            onClick={cancelClicked}
          />
        </div>

        <div className="p-8 overflow-y-auto flex-1">
          <div className="flex flex-col gap-4">
            <Field
              label="Name"
              required
              value={name}
              onChange={setName}
              placeholder="Enter name"
              error={nameError}
            />
            <Field
              label="Email"
              required
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="Enter email"
              error={emailError}
            />
            <Field
              label="Phone Number"
              value={phone}
              onChange={(v) => setPhone(formatUSPhone(v))}
              placeholder="(xxx) xxx-xxxx"
            />
            <div className="flex flex-col gap-1">
              <label className="block text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                Role <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {roles.map((item) => {
                  const selected = item.id === roleId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setRoleId(item.id)}
                      className={`text-left px-4 py-3 rounded-[8px] border cursor-pointer transition-colors ${
                        selected
                          ? "border-blueColor bg-[#F0F6FF]"
                          : roleError
                            ? "border-red-500 bg-white hover:bg-[#F8F9FE]"
                            : "border-[#E5E8F3] bg-white hover:bg-[#F8F9FE]"
                      }`}
                    >
                      <p
                        className={`text-[14px] leading-[20px] font-OpenSansSemiBold ${
                          selected ? "text-blueColor" : "text-[#1A2332]"
                        }`}
                      >
                        {item.name}
                      </p>
                      {CUSTOMER_ROLE_INFO[item.name] && (
                        <p className="mt-1 text-[12px] leading-[18px] font-OpenSansRegular text-[#5F6477]">
                          {CUSTOMER_ROLE_INFO[item.name]}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
              {roleError && (
                <p className="text-[12px] text-red-500 font-OpenSansRegular">
                  Choose a role.
                </p>
              )}
            </div>
            {needsLocations && (
              <div className="flex flex-col gap-1">
                <MultiSelectChipInput
                  label="Locations"
                  required
                  error={locationError}
                  options={locationOptions}
                  selectedIds={locationIds}
                  onChange={setLocationIds}
                  placeholder="Search locations..."
                />
                {locationError && (
                  <p className="text-[12px] text-red-500 font-OpenSansRegular">
                    Select at least one location for this Manager.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
          <button
            type="button"
            onClick={cancelClicked}
            className={`h-[40px] px-1 rounded-[6px] text-[16px] leading-[24px] capitalize font-OpenSansSemiBold text-[#7C829A] ${
              isInviting ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={addClicked}
            className={`h-[40px] px-4 rounded-[8px] text-[16px] leading-[24px] capitalize font-OpenSansSemiBold bg-blueColor text-white flex items-center justify-center ${
              isInviting ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {isInviting ? (
              <Oval
                visible={true}
                height="18"
                width="18"
                color="#FFFFFF"
                strokeWidth={8}
                secondaryColor="#FFEEFF"
                ariaLabel="invite-loading"
              />
            ) : (
              "Send Invite"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = "text",
  error,
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="block text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-[40px] px-3 rounded-[6px] border text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#1A2332] focus:border-blueColor ${
          error ? "border-red-500" : "border-[#E5E8F3]"
        }`}
      />
    </div>
  );
}

export default AdminAddModal;
